
import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { User } from "@supabase/supabase-js";
import { User as UserIcon, LogOut, Settings, CreditCard, HelpCircle, Plane } from "lucide-react";

interface MobileNavigationProps {
  isOpen: boolean;
  onClose: () => void;
  user: User | null;
  onSignOut: () => void;
  onAuthClick: () => void;
  onNewRequestClick: () => void;
}

export const MobileNavigation = ({
  isOpen,
  onClose,
  user,
  onSignOut,
  onAuthClick,
  onNewRequestClick,
}: MobileNavigationProps) => {
  if (!isOpen) return null;

  const publicNavItems = [
    { label: "Features", path: "/features" },
    { label: "Pricing", path: "/pricing" },
    { label: "FAQ", path: "/faq" }, // Available for all users
  ];

  const getUserNavItems = () => {
    const baseItems = [
      { label: "Dashboard", path: "/dashboard" },
      { label: "New Request", path: "/refund" },
      { label: "Conversations", path: "/conversations" },
      { label: "FAQ", path: "/faq" }, // Available for authenticated users too
    ];
    
    return baseItems;
  };

  const userNavItems = getUserNavItems();

  return (
    <div className="md:hidden border-t border-slate-200 bg-white">
      <div className="px-4 py-4 space-y-4">
        {user ? (
          // Authenticated user navigation
          <>
            <div className="space-y-3">
              {userNavItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={onClose}
                  className="block text-slate-600 hover:text-primary font-medium"
                >
                  {item.label}
                </Link>
              ))}
            </div>
            
            <div className="border-t border-slate-200 pt-4">
              <div className="flex items-center gap-2 mb-4">
                <UserIcon className="h-4 w-4" />
                <span className="text-sm font-medium">{user.email}</span>
              </div>
              
              <div className="space-y-3">
                <Link
                  to="/profile"
                  onClick={onClose}
                  className="flex items-center gap-2 text-slate-600 hover:text-primary"
                >
                  <UserIcon className="h-4 w-4" />
                  <span>Profile</span>
                </Link>
                
                <Link
                  to="/pricing"
                  onClick={onClose}
                  className="flex items-center gap-2 text-slate-600 hover:text-primary"
                >
                  <CreditCard className="h-4 w-4" />
                  <span>Subscription</span>
                </Link>
                
                <Link
                  to="/faq"
                  onClick={onClose}
                  className="flex items-center gap-2 text-slate-600 hover:text-primary"
                >
                  <HelpCircle className="h-4 w-4" />
                  <span>FAQ</span>
                </Link>
                
                <button
                  onClick={() => {
                    onSignOut();
                    onClose();
                  }}
                  className="flex items-center gap-2 text-slate-600 hover:text-primary w-full text-left"
                >
                  <LogOut className="h-4 w-4" />
                  <span>Sign out</span>
                </button>
              </div>
            </div>
          </>
        ) : (
          // Public navigation
          <>
            <div className="space-y-3">
              {publicNavItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={onClose}
                  className="block text-slate-600 hover:text-primary font-medium"
                >
                  {item.label}
                </Link>
              ))}
            </div>
            
            <div className="border-t border-slate-200 pt-4 space-y-3">
              <Button
                variant="ghost"
                onClick={() => {
                  onAuthClick();
                  onClose();
                }}
                className="w-full justify-start"
              >
                Sign In
              </Button>
              <Button
                onClick={() => {
                  onNewRequestClick();
                  onClose();
                }}
                className="w-full"
              >
                Get Started
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
